import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTourStore } from '@/stores/tour';

/**
 * The guided tour, drawn over the application (§11.2).
 *
 * A spotlight and a card, and the whole design follows from one decision: the
 * tour points at the *real* interface rather than at pictures of it. Somebody
 * finishing this has already seen the screen they will use tomorrow, in the
 * state it is actually in, with their own data behind it.
 *
 * That decision is what makes the rest necessary. The cut-out has to follow a
 * live element through scrolling, resizing and language changes; the card has to
 * find room beside it in either reading direction; and a step whose target is
 * missing — because the reader's permissions hide that menu item — has to move
 * on rather than pointing confidently at an empty corner.
 *
 * **It is a dialog, and behaves like one.** Focus moves into the card, Escape
 * leaves, arrow keys move between steps, and the page behind is inert to the
 * screen reader. A tour that traps a keyboard user is worse than no tour.
 */

/** How long to wait for a target to appear before giving up on the step. */
const TARGET_TIMEOUT_MS = 1200;

/** Breathing room around the spotlight, in pixels. */
const PADDING = 8;

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

/** Has the spotlight actually moved? Sub-pixel jitter is not movement. */
function hasMoved(a: Rect, b: Rect): boolean {
    return (
        Math.abs(a.top - b.top) > 0.5 ||
        Math.abs(a.left - b.left) > 0.5 ||
        Math.abs(a.width - b.width) > 0.5 ||
        Math.abs(a.height - b.height) > 0.5
    );
}

function findTarget(key: string | null): HTMLElement | null {
    if (key === null) {
        return null;
    }

    return document.querySelector<HTMLElement>(`[data-tour="${key}"]`);
}

export function ProductTour() {
    const { i18n } = useTranslation('tour');
    const isArabic = i18n.language === 'ar';

    const tour = useTourStore((state) => state.tour);
    const isActive = useTourStore((state) => state.isActive);
    const index = useTourStore((state) => state.index);
    const next = useTourStore((state) => state.next);
    const back = useTourStore((state) => state.back);
    const dismiss = useTourStore((state) => state.dismiss);
    const finish = useTourStore((state) => state.finish);

    const offer = useTourStore((state) => state.offer);

    const navigate = useNavigate();
    const location = useLocation();

    const [rect, setRect] = useState<Rect | null>(null);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const offered = useRef(false);

    /*
     * Offered once, on the first authenticated screen of the session.
     *
     * The store decides whether it actually runs: somebody who has already seen
     * this version gets nothing, and somebody who left early is not asked again.
     * A tour that reappears every morning teaches people to dismiss whatever the
     * platform says next.
     */
    useEffect(() => {
        if (offered.current) {
            return;
        }

        offered.current = true;
        void offer();
    }, [offer]);

    const step = tour?.steps[index] ?? null;
    const total = tour?.steps.length ?? 0;
    const isLast = index === total - 1;

    /**
     * Follow the target.
     *
     * Measured on a frame loop rather than once: the sidebar animates when it
     * collapses, the page scrolls, and a language change re-lays out the whole
     * shell. A rectangle measured once would drift off its element within a
     * second of anything moving.
     */
    useLayoutEffect(() => {
        if (!isActive || step === null) {
            return;
        }

        let frame = 0;
        let elapsed = 0;
        let cancelled = false;
        let previous: Rect | null = null;

        const measure = (timestamp: number, previousTime: number) => {
            if (cancelled) {
                return;
            }

            const element = findTarget(step.target);

            if (element === null) {
                elapsed += timestamp - previousTime;

                // The target may simply not exist for this reader. Rather than
                // spotlighting nothing, move on.
                if (step.target !== null && elapsed > TARGET_TIMEOUT_MS) {
                    setRect(null);

                    if (index < total - 1) {
                        next();
                    }

                    return;
                }

                if (previous !== null) {
                    previous = null;
                    setRect(null);
                }
            } else {
                const box = element.getBoundingClientRect();

                const measured: Rect = {
                    top: box.top - PADDING,
                    left: box.left - PADDING,
                    width: box.width + PADDING * 2,
                    height: box.height + PADDING * 2,
                };

                /*
                 * Only when it has actually moved.
                 *
                 * The loop runs every frame so the spotlight can follow a
                 * scrolling or animating element, but setting state on each one
                 * would re-render the card sixty times a second while nothing
                 * changes — burning battery, and making a button that is being
                 * clicked a moving target for anything measuring it.
                 */
                if (previous === null || hasMoved(previous, measured)) {
                    previous = measured;
                    setRect(measured);
                }
            }

            frame = window.requestAnimationFrame((now) => measure(now, timestamp));
        };

        frame = window.requestAnimationFrame((now) => measure(now, now));

        return () => {
            cancelled = true;
            window.cancelAnimationFrame(frame);
        };
    }, [isActive, step, index, total, next]);

    /*
     * Open the screen the step is about, if it is not already open.
     *
     * This is what makes it a tour of the platform rather than a tour of the
     * menu: the reader ends up looking at the real screen, with their own data
     * on it, rather than at a description of it. The spotlight then waits for
     * the target to appear — and gives up on it rather than hanging if the new
     * screen does not have one.
     */
    useEffect(() => {
        if (!isActive || step === null || step.route === null) {
            return;
        }

        if (location.pathname !== step.route) {
            void navigate(step.route);
        }
    }, [isActive, step, location.pathname, navigate]);

    /** Bring the target into view before spotlighting it. */
    useEffect(() => {
        if (!isActive || step?.target == null) {
            return;
        }

        findTarget(step.target)?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                ? 'auto'
                : 'smooth',
            block: 'center',
        });
    }, [isActive, step]);

    /** Focus the card, so the keyboard follows the tour. */
    useEffect(() => {
        if (isActive) {
            cardRef.current?.focus();
        }
    }, [isActive, index]);

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                dismiss();

                return;
            }

            // Arrow keys follow the reading direction, so "forward" is the same
            // physical key it would be in a book.
            const forward = isArabic ? 'ArrowLeft' : 'ArrowRight';
            const backward = isArabic ? 'ArrowRight' : 'ArrowLeft';

            if (event.key === forward) {
                event.preventDefault();
                next();
            }

            if (event.key === backward) {
                event.preventDefault();
                back();
            }
        },
        [dismiss, next, back, isArabic],
    );

    if (!isActive || tour === null || step === null) {
        return null;
    }

    const centred = step.placement === 'center' || rect === null;

    return createPortal(
        <div
            className="fixed inset-0 z-[100]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tour-title"
            aria-describedby="tour-body"
            onKeyDown={onKeyDown}
        >
            {/*
             * The dim, cut out around the target.
             *
             * A single element with an enormous spread shadow rather than four
             * panels or an SVG mask: it is one box to animate, so the spotlight
             * glides between steps instead of four edges arriving separately.
             */}
            {rect ? (
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute rounded-xl ring-2 ring-[var(--brand-accent)] transition-all duration-300 ease-out motion-reduce:transition-none"
                    style={{
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                        boxShadow: '0 0 0 9999px rgba(6, 11, 30, 0.72)',
                    }}
                />
            ) : (
                <div
                    aria-hidden="true"
                    className="absolute inset-0"
                    style={{ backgroundColor: 'rgba(6, 11, 30, 0.72)' }}
                />
            )}

            {/*
             * Clicking the dim leaves the tour. Somebody reaching past it for a
             * control has told us they would rather be using the platform, and
             * arguing with them by ignoring the click is not a good first
             * impression.
             */}
            <div
                /*
                 * A click target, not a control. Announcing it as a third button
                 * called "skip the tour" would put three identically named items
                 * in a screen reader's button list for one action; the keyboard
                 * ways out are Escape and the two real buttons on the card.
                 */
                aria-hidden="true"
                className="absolute inset-0 cursor-default"
                onClick={dismiss}
            />

            <TourCard
                ref={cardRef}
                rect={rect}
                centred={centred}
                placement={step.placement}
                isArabic={isArabic}
                title={isArabic ? step.title_ar : step.title_en}
                body={isArabic ? step.body_ar : step.body_en}
                index={index}
                total={total}
                isLast={isLast}
                onNext={next}
                onBack={back}
                onSkip={dismiss}
                onFinish={finish}
            />
        </div>,
        document.body,
    );
}

/* ---- The card ------------------------------------------------------------ */

const CARD_WIDTH = 380;
const GAP = 16;

/**
 * Where the card sits relative to the spotlight.
 *
 * Clamped to the viewport at the end, because the honest answer to "there is no
 * room on that side" is to put it where it fits rather than half off the screen.
 */
function positionFor(
    rect: Rect | null,
    placement: string,
    isArabic: boolean,
): React.CSSProperties {
    if (rect === null) {
        return {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: `min(${CARD_WIDTH}px, calc(100vw - 2rem))`,
        };
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(CARD_WIDTH, viewportWidth - 32);

    let top = rect.top;
    let left: number;

    if (placement === 'bottom') {
        top = rect.top + rect.height + GAP;
        left = rect.left + rect.width / 2 - width / 2;
    } else if (placement === 'top') {
        top = rect.top - GAP - 200;
        left = rect.left + rect.width / 2 - width / 2;
    } else {
        // `end` means away from the element on the reading side: to its right in
        // English, to its left in Arabic.
        left = isArabic ? rect.left - width - GAP : rect.left + rect.width + GAP;
    }

    // If it does not fit on the chosen side, put it on the other one.
    if (left < 16) {
        left = rect.left + rect.width + GAP;
    }

    if (left + width > viewportWidth - 16) {
        left = rect.left - width - GAP;
    }

    return {
        top: Math.max(16, Math.min(top, viewportHeight - 260)),
        left: Math.max(16, Math.min(left, viewportWidth - width - 16)),
        width,
    };
}

function TourCard({
    ref,
    rect,
    centred,
    placement,
    isArabic,
    title,
    body,
    index,
    total,
    isLast,
    onNext,
    onBack,
    onSkip,
    onFinish,
}: {
    ref: React.Ref<HTMLDivElement>;
    rect: Rect | null;
    centred: boolean;
    placement: string;
    isArabic: boolean;
    title: string;
    body: string;
    index: number;
    total: number;
    isLast: boolean;
    onNext: () => void;
    onBack: () => void;
    onSkip: () => void;
    onFinish: () => void;
}) {
    const { t } = useTranslation('tour');

    const Forward = isArabic ? ArrowLeft : ArrowRight;

    return (
        <div
            ref={ref}
            tabIndex={-1}
            className={cn(
                'absolute rounded-2xl border border-white/10 bg-card p-5 shadow-2xl outline-none transition-all duration-300 ease-out motion-reduce:transition-none',
                centred && 'shadow-black/40',
            )}
            style={positionFor(centred ? null : rect, placement, isArabic)}
        >
            <span
                aria-hidden="true"
                className="absolute inset-x-5 top-0 h-0.5 rounded-full bg-gradient-to-r from-transparent via-[var(--brand-accent)] to-transparent"
            />

            <div className="flex items-start justify-between gap-3">
                <p className="text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase tabular-nums">
                    {t('step_of', { current: index + 1, total })}
                </p>

                <Button
                    size="icon"
                    variant="ghost"
                    className="-me-2 -mt-2 size-7"
                    onClick={onSkip}
                >
                    <X className="size-3.5" aria-hidden="true" />
                    <span className="sr-only">{t('close')}</span>
                </Button>
            </div>

            <h2 id="tour-title" className="mt-2 text-lg font-semibold text-balance">
                {title}
            </h2>

            <p
                id="tour-body"
                className="mt-2 text-sm leading-relaxed text-muted-foreground"
            >
                {body}
            </p>

            {/* Progress, as dots — legible at a glance and countable. */}
            <div className="mt-4 flex items-center gap-1.5" aria-hidden="true">
                {Array.from({ length: total }, (_, dot) => (
                    <span
                        key={dot}
                        className={cn(
                            'h-1.5 rounded-full transition-all duration-300 motion-reduce:transition-none',
                            dot === index
                                ? 'w-5 bg-[var(--brand-accent)]'
                                : dot < index
                                  ? 'w-1.5 bg-[var(--brand-accent)]/50'
                                  : 'w-1.5 bg-muted-foreground/25',
                        )}
                    />
                ))}
            </div>

            <div className="mt-5 flex items-center justify-between gap-2">
                <Button variant="ghost" size="sm" onClick={onSkip}>
                    {t('skip')}
                </Button>

                <div className="flex items-center gap-2">
                    {index > 0 ? (
                        <Button variant="outline" size="sm" onClick={onBack}>
                            {t('back')}
                        </Button>
                    ) : null}

                    <Button size="sm" onClick={isLast ? onFinish : onNext}>
                        {isLast ? t('finish') : t('next')}
                        {isLast ? null : (
                            <Forward className="size-3.5" aria-hidden="true" />
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
